import { useState } from "react";
import { useGetFlowTemplatesQuery } from "../../store/apiSlices/flowTemplates";
import { DefaultFlowTemplateData } from "../../types/FlowTemplates";
import ActiveFlowTemplateCard from "./FlowTemplate/ActiveFlowTemplateCard";
import CustomTemplateCard from "./FlowTemplate/CustomTemplateCard";
import FlowTemplateCarousel from "./FlowTemplate/FlowTemplateCarousel";
import { useTransitionPresence } from "../../hooks/useTransitionPresence";

export default function HeaderBtns() {
  const [activeTemplate, setActiveTemplate] =
    useState<DefaultFlowTemplateData | null>(null);
  const [isActiveTemplateOpen, setIsActiveTemplateOpen] = useState(false);
  const [isCustomTemplateOpen, setIsCustomTemplateOpen] = useState(false);

  const {
    data: allFlowTemplates,
    isLoading,
    isError,
  } = useGetFlowTemplatesQuery();

  const {
    ref: customTemplateContainerRef,
    shouldRender: shouldCustomTemplateRender,
  } = useTransitionPresence(isCustomTemplateOpen);

  const {
    ref: activeTemplateContainerRef,
    shouldRender: shouldActiveTemplateRender,
  } = useTransitionPresence(isActiveTemplateOpen, () => {
    setActiveTemplate(null);
  });

  const handleClick = (id: number) => {
    if (!allFlowTemplates) return;

    const activeData = allFlowTemplates.find((el) => el.id === id);

    if (!activeData) return;

    setActiveTemplate(activeData);
    setIsActiveTemplateOpen(true);
  };

  const onClose = () => {
    setIsActiveTemplateOpen(false);
  };

  const toggleCustomTemplate = () => {
    setIsCustomTemplateOpen((prev) => !prev);
  };

  if (isLoading) return <FlowTemplatesBtnSkeleton />;

  if (isError)
    return (
      <p className="text-center text-gray-600">
        Failed to load default templates
      </p>
    );

  if (!allFlowTemplates)
    return <p className="text-center text-gray-600">No data found</p>;

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-2 lg:gap-0 mb-6">
        <button
          type="button"
          className={`border-dashed border-2 ${
            isCustomTemplateOpen
              ? "border-custom-blue-medium"
              : "border-[#D0D5DD]"
          } rounded-xl px-4 py-2 text-[#344054] font-medium bg-white shadow-sm transition-colors duration-200`}
          onClick={toggleCustomTemplate}
          id="create-new-flow-template-btn"
        >
          + Create New Flow Template
        </button>

        <FlowTemplateCarousel
          allFlowTemplates={allFlowTemplates}
          activeTemplate={activeTemplate}
          handleClick={handleClick}
        />
      </div>

      <div
        ref={activeTemplateContainerRef}
        className={`
         transition-all duration-300 ease-in-out overflow-hidden
         ${
           isActiveTemplateOpen
             ? "max-h-[256px] opacity-100 mb-2"
             : "max-h-0 opacity-0 mb-0"
         }
        `}
      >
        {shouldActiveTemplateRender && activeTemplate && (
          <ActiveFlowTemplateCard
            activeTemplate={activeTemplate}
            onClose={onClose}
          />
        )}
      </div>

      <div
        ref={customTemplateContainerRef}
        className={`
         transition-all duration-300 ease-in-out overflow-hidden
         ${
           isCustomTemplateOpen
             ? "max-h-[256px] opacity-100 mb-2"
             : "max-h-0 opacity-0 mb-0"
         }
        `}
      >
        {shouldCustomTemplateRender && (
          <CustomTemplateCard toggleCustomTemplate={toggleCustomTemplate} />
        )}
      </div>
    </section>
  );
}

function FlowTemplatesBtnSkeleton() {
  return (
    <div className="animate-pulse flex items-center justify-between mb-6">
      <div className="rounded-xl bg-slate-300 h-11 w-[266px]"></div>
      <div className="flex gap-2">
        {[1, 2, 3, 4].map((el) => (
          <div key={el} className="h-11 w-[120px] bg-slate-300 rounded"></div>
        ))}
      </div>
    </div>
  );
}
